import {
  EducationControllerService,
  EducationJourneyProgramStatusResponse,
  TaskResponse,
  UpdateTaskRequest
} from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import {
  AppRemoteConfigCubit,
  JourneyContentResponse
} from "src/state/AppRemoteConfigCubit/AppRemoteConfigCubit";
import { KnownProgram } from "src/state/ProgramBloc/ProgramBloc";
import { taskManagementState, userState } from "src/state/state";
import {
  TaskKey,
  TaskObserverEvent,
  TaskResponseKnown
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import {
  ColorScheme,
  moduleNameToSchemaMap
} from "components/UserJourneyOverview/lib";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { tracker } from "src/state/state";
import { ProfileProgramMembership } from "src/state/UserCubit/UserCubit";
import translate from "src/lib/translate";

export type JourneyType =
  | "mindset-journey"
  | "physical-activity-journey"
  | "nutrition-journey";

export enum Journey {
  MINDSET = "mindset-journey",
  PHYSICAL_ACTIVITY = "physical-activity-journey",
  NUTRITION = "nutrition-journey"
}

export interface UserJourneyLessonItem {
  type: "typeform" | "article" | "coaching";
  typeformId?: string;
  url?: string;
  task?: TaskResponseKnown;
}

export interface UserJourneyLesson {
  name: string;
  taskGroup?: string;
  items: UserJourneyLessonItem[];
}

export interface UserJourneyModule {
  name: string;
  theme: ColorScheme;
  subtitle: string;
  group: string;
  lessons: UserJourneyLesson[];
  index: number;
  totalModules: number;
}

export interface UserJourneyState {
  enableAll: boolean;
  modules?: UserJourneyModule[];
  name?: string;
  loading?: boolean;
  journeys: EducationJourneyProgramStatusResponse[];
  journeyCardsVisible?: boolean;
  journeyCardsLocked?: boolean;
}

interface UserJourneyProps {
  journey: string;
  taskProgram: string;
}

export default class UserJourneyBloc extends Cubit<
  UserJourneyState,
  UserJourneyProps
> {
  tasks: TaskResponseKnown[] = [];
  journeyContents: Map<
    string,
    JourneyContentResponse & { programKey: string }
  > = new Map();

  private readonly JourneyStatus =
    EducationJourneyProgramStatusResponse.journeyStatus;

  static getProgramFromMembership = (): KnownProgram | null => {
    if (!userState.programMemberships) {
      return null;
    }

    const activeProgram = userState.programMemberships.find(
      (membership) =>
        membership.active || (!membership.start && !membership.end)
    )?.program;

    switch (activeProgram) {
      case ProfileProgramMembership.EVERYDAY_DIABETES_JOURNEY:
        return KnownProgram.EVERYDAY_DIABETES_JOURNEY;
      case ProfileProgramMembership.HEALTHY_WEIGHT_JOURNEY:
        return KnownProgram.LIFEBALANCE;
      default:
        return KnownProgram.LIFEBALANCE;
    }
  };

  static getTitleFromMembership = (): string | null => {
    if (!userState.programMemberships) {
      return null;
    }

    const activeProgram = userState.programMemberships.find(
      (membership) =>
        membership.active || (!membership.start && !membership.end)
    )?.program;

    switch (activeProgram) {
      case ProfileProgramMembership.EVERYDAY_DIABETES_JOURNEY:
        return translate("lifestyle.title_everydayDiabetesJourney");
      case ProfileProgramMembership.HEALTHY_WEIGHT_JOURNEY:
        return translate("lifestyle.title_lifebalance");
      default:
        return translate("lifestyle.title_lifebalance");
    }
  };

  constructor(props: UserJourneyProps) {
    super({
      enableAll: false,
      journeys: []
    });
    this.props = props;
    void this.loadContent();
  }

  loadContent = async () => {
    if (!this.props?.journey || !this.props?.taskProgram) return;

    this.patch({ loading: true });

    try {
      await Promise.all([
        this.load12weekJourney(),
        this.loadAdvancedJourneysData()
      ]);

      this.prepareState();
    } catch (error) {
      reportErrorSentry(error as Error);
    } finally {
      this.patch({ loading: false });
      this.addObserverForTaskChanges();
    }
  };

  private load12weekJourney = async () => {
    if (!this.props?.journey || !this.props?.taskProgram) return;

    const { journey, taskProgram } = this.props;

    await Promise.all([
      this.loadJourney(journey),
      this.loadTasksForPrograms([taskProgram as KnownProgram])
    ]);
  };

  private loadAdvancedJourneysData = async () => {
    await this.getJourneysStatus();
    await this.isChooseNextJourneyAvailable();

    const activeJourneyNames = this.getActiveJourneyNames();

    if (activeJourneyNames.length > 0) {
      await Promise.all([
        this.loadJourneysContent(activeJourneyNames),
        this.loadTasksForPrograms(activeJourneyNames)
      ]);
    }
  };

  private loadJourneysContent = async (journeyNames: KnownProgram[]) => {
    await Promise.all(
      journeyNames.map((journeyName) => this.loadJourney(journeyName))
    );
  };

  private loadTasksForPrograms = async (programs: KnownProgram[]) => {
    const allTasks = await Promise.all(
      programs.map((program) => taskManagementState.loadProgramTasks(program))
    );

    // Merge tasks without duplicates
    const newTasks = allTasks.flat().filter(Boolean) as TaskResponseKnown[];
    this.mergeTasks(newTasks);
  };

  private sortByJourneyEnrollmentOrder = (
    programA: string,
    programB: string
  ): number => {
    // If same program, maintain original order
    if (programA === programB) {
      return 0;
    }

    // 12w journey - handle both journey key and task program
    const is12wProgramA =
      programA === this.props?.journey || programA === this.props?.taskProgram;
    const is12wProgramB =
      programB === this.props?.journey || programB === this.props?.taskProgram;

    if (is12wProgramA && !is12wProgramB) {
      return -1;
    }
    if (!is12wProgramA && is12wProgramB) {
      return 1;
    }

    // Sort other programs by enrollment order (updatedAt of enroll task)
    // For journey content programs, map to task program for enroll task lookup
    const getTaskProgramForEnrollLookup = (program: string) => {
      if (program === this.props?.journey) return this.props?.taskProgram;
      return program;
    };

    const enrollTaskA = this.tasks.find(
      (t) =>
        t?.program === getTaskProgramForEnrollLookup(programA) &&
        t?.group === "user-experience" &&
        t?.slug === "enroll"
    );
    const enrollTaskB = this.tasks.find(
      (t) =>
        t?.program === getTaskProgramForEnrollLookup(programB) &&
        t?.group === "user-experience" &&
        t?.slug === "enroll"
    );

    // If either task doesn't have updatedAt, put it at the end
    if (!enrollTaskA?.updatedAt) return 1;
    if (!enrollTaskB?.updatedAt) return -1;

    // Sort by updatedAt (earliest enrolled first)
    return (
      new Date(enrollTaskA.updatedAt).getTime() -
      new Date(enrollTaskB.updatedAt).getTime()
    );
  };

  private mergeTasks = (newTasks: TaskResponseKnown[]) => {
    const existingTaskKeys = new Set(
      this.tasks.map((task) => `${task.program}-${task.group}-${task.slug}`)
    );

    const uniqueNewTasks = newTasks.filter(
      (task) =>
        !existingTaskKeys.has(`${task.program}-${task.group}-${task.slug}`)
    );

    this.tasks = [...this.tasks, ...uniqueNewTasks];

    // Sort all tasks by journey enrollment order
    this.tasks.sort((taskA, taskB) => {
      const programA = taskA?.program;
      const programB = taskB?.program;
      return this.sortByJourneyEnrollmentOrder(programA, programB);
    });
  };

  private getJourneysWithStatuses(
    statuses: EducationJourneyProgramStatusResponse.journeyStatus[]
  ): EducationJourneyProgramStatusResponse[] {
    return this.state.journeys.filter((j) =>
      statuses.includes(j.journeyStatus)
    );
  }

  private getActiveJourneyNames = (): KnownProgram[] => {
    return this.getJourneysWithStatuses([this.JourneyStatus.IN_PROGRESS]).map(
      (j) => j.taskProgramName as KnownProgram
    );
  };

  onTasksChanged = (programs: Map<KnownProgram, TaskResponseKnown[]>): void => {
    if (!this.props?.taskProgram) return;

    const mainProgram = this.props.taskProgram as KnownProgram;
    const activePrograms = this.getActiveJourneyNames();
    const allPrograms = [mainProgram, ...activePrograms];

    const allUpdatedTasks: TaskResponseKnown[] = [];
    allPrograms.forEach((program) => {
      const programTasks = programs.get(program);
      if (programTasks) {
        allUpdatedTasks.push(...programTasks);
      }
    });

    if (allUpdatedTasks.length > 0) {
      this.tasks = allUpdatedTasks;
      // Sort tasks by journey enrollment order to maintain consistent numbering
      this.tasks.sort((taskA, taskB) => {
        const programA = taskA?.program;
        const programB = taskB?.program;
        return this.sortByJourneyEnrollmentOrder(programA, programB);
      });
      this.prepareState();
    }
  };

  addObserverForTaskChanges = (): void => {
    taskManagementState.addObserver(
      TaskObserverEvent.TASK_LIST_CHANGED,
      this.onTasksChanged
    );
  };

  loadJourney = async (journey: string) => {
    const response =
      await Blac.getBloc(AppRemoteConfigCubit).loadJourneyContent(journey);
    this.journeyContents.set(journey, {
      ...response.journey,
      programKey: journey
    });

    // Only update name for the main journey
    if (journey === this.props?.journey) {
      this.patch({ name: response.journey.name });
    }
  };

  prepareState = () => {
    if (this.journeyContents.size === 0) return;

    const sortedJourneyContents = Array.from(
      this.journeyContents.values()
    ).sort((a, b) =>
      this.sortByJourneyEnrollmentOrder(a.programKey, b.programKey)
    );

    const allModules: UserJourneyModule[] = [];
    let globalIndex = 0;

    sortedJourneyContents.forEach((journeyContent) => {
      const classes = journeyContent.listOfClassesContentAvailable;

      const journeyModules = classes.map((c) => {
        const lessons = c.listOfLessons.map((l) => ({
          name: l.name,
          taskGroup: l.taskGroup,
          items: l.items
            .map((i) => ({
              type: i.type,
              typeformId: i.typeformId,
              taskSlug: i.taskSlug,
              url: i.url,
              task: this.tasks.find((t) => {
                return t?.slug === i.taskSlug && t?.group === l.taskGroup;
              })
            }))
            .filter((i) => Boolean(i.task))
        }));

        return {
          name: c.name,
          theme: moduleNameToSchemaMap.get(c.name) as ColorScheme,
          index: globalIndex++, // Use global index and increment
          totalModules: classes.length,
          subtitle: c.subtitle,
          group: c.taskGroupReference,
          lessons
        };
      });

      allModules.push(...journeyModules);
    });

    this.patch({ modules: allModules });
  };

  getJourneysStatus = async () => {
    try {
      const response = await EducationControllerService.getEducationJourneys();

      const journeysData = response?.data || [];
      this.patch({ journeys: journeysData });
    } catch (error: unknown) {
      this.patch({ journeys: [] });
      reportErrorSentry(error as Error);
    }
  };

  enrollInJourney = async (
    journey: JourneyType,
    source: "journey-card" | "journey-modal"
  ) => {
    this.patch({ loading: true });

    try {
      await taskManagementState.updateTaskStatus(
        {
          program: KnownProgram.EDUCATION,
          group: "journeys",
          slug: "choose-next-journey"
        },
        UpdateTaskRequest.status.COMPLETED,
        true //We want to skip the reload of the tasks for the education program, since we need to enroll in the journey first
      );

      // Update task status for the specific journey
      await taskManagementState.updateTaskStatus(
        {
          program: journey,
          group: "user-experience",
          slug: "enroll"
        },
        UpdateTaskRequest.status.COMPLETED
      );

      const journeysCompleted = this.state.journeys
        .filter((j) => j.journeyStatus === this.JourneyStatus.COMPLETED)
        .map((j) => j.taskProgramName);
      const journeysInProgress = this.state.journeys
        .filter((j) => j.journeyStatus === this.JourneyStatus.IN_PROGRESS)
        .map((j) => j.taskProgramName);
      const journeysAvailable = this.state.journeys
        .filter((j) => j.journeyStatus === this.JourneyStatus.AVAILABLE)
        .map((j) => j.taskProgramName);

      tracker.track("Enroll in Journey", {
        data: {
          journey: journey,
          enrollSource: source,
          journeyStatuses: this.state.journeys.map(
            (j) => `${j.taskProgramName} - ${j.journeyStatus}`
          ),
          journeysCompleted: journeysCompleted,
          journeysInProgress: journeysInProgress,
          journeysAvailable: journeysAvailable,
          noOfJourneysCompleted: journeysCompleted.length,
          noOfJourneysInProgress: journeysInProgress.length,
          noOfJourneysAvailable: journeysAvailable.length
        }
      });

      // Reload data to reflect the new enrollment
      await Promise.all([
        this.getJourneysStatus(),
        this.loadAdvancedJourneysData()
      ]);

      this.prepareState();
    } catch (error: unknown) {
      reportErrorSentry(error as Error);
    } finally {
      this.patch({ loading: false });
    }
  };

  isChooseNextJourneyAvailable = async () => {
    try {
      await taskManagementState.loadProgramTasks(KnownProgram.EDUCATION);
      const task = taskManagementState.getTask(TaskKey.CHOOSE_NEXT_JOURNEY);
      const chooseNextJourneyAvailable =
        task?.status === TaskResponse.status.AVAILABLE;

      this.patch({
        journeyCardsLocked: !chooseNextJourneyAvailable
      });
    } catch (error: unknown) {
      reportErrorSentry(error as Error);
    }
  };

  private isJourneyCardVisible = (journey: JourneyType) => {
    const isJourneyAvailable = this.getJourneysWithStatuses([
      this.JourneyStatus.AVAILABLE,
      this.JourneyStatus.LOCKED
    ]).some((j) => j.taskProgramName === journey);
    return isJourneyAvailable;
  };

  get isNutritionJourneyCardVisible() {
    return this.isJourneyCardVisible(Journey.NUTRITION);
  }

  get isMindsetJourneyCardVisible() {
    return this.isJourneyCardVisible(Journey.MINDSET);
  }

  get isActivityJourneyCardVisible() {
    return this.isJourneyCardVisible(Journey.PHYSICAL_ACTIVITY);
  }

  get availableJourneysToEnroll() {
    if (this.props?.journey === KnownProgram.EVERYDAY_DIABETES_JOURNEY) {
      return false;
    }

    const allAdvancedJourneys = this.state.journeys.filter(
      (j) => j.taskProgramName !== KnownProgram.EVERYDAY_DIABETES_JOURNEY
    );

    const allJourneysInProgress = allAdvancedJourneys.filter(
      (j) => j.journeyStatus === this.JourneyStatus.IN_PROGRESS
    );

    return !(allJourneysInProgress.length === allAdvancedJourneys.length);
  }

  onTaskStatusUpdated = (
    task: TaskResponseKnown | undefined,
    status: string
  ) => {
    if (!task) return;
    this.tasks = this.tasks.map((t) => {
      if (
        t?.program === task.program &&
        t.group === task.group &&
        t.slug === task.slug
      ) {
        return {
          ...t,
          status
        } as TaskResponseKnown;
      }

      return t;
    });
    this.prepareState();
  };

  isNextAvailableTask = (task: TaskResponseKnown | undefined) => {
    const noAvailableOrInProgressTasks =
      this.tasks.find(
        (task) =>
          task?.status === TaskResponse.status.AVAILABLE ||
          task?.status === TaskResponse.status.IN_PROGRESS
      ) === undefined;
    const completedTask = this.tasks.find(
      (task) => task?.status === TaskResponse.status.COMPLETED
    );
    const firstLockedTask = this.tasks.find(
      (task) => task?.status === TaskResponse.status.LOCKED
    );
    const isLocked = task?.status === TaskResponse.status.LOCKED;

    if (
      isLocked &&
      task.slug === firstLockedTask?.slug &&
      noAvailableOrInProgressTasks &&
      completedTask
    ) {
      return true;
    }
    return false;
  };

  get questionnaireTasks(): TaskResponseKnown[] {
    // Ensure tasks are sorted by journey enrollment order for consistent numbering
    const sortedTasks = [...this.tasks].sort((taskA, taskB) => {
      const programA = taskA?.program;
      const programB = taskB?.program;
      return this.sortByJourneyEnrollmentOrder(programA, programB);
    });

    return sortedTasks.filter((t) =>
      t?.slug.includes("questionnaire")
    ) as TaskResponseKnown[];
  }
}
